import {
  PaymentWorkflowType,
  SECTION_KEY_MAP,
  FORM_TO_SECTION_KEY_MAP,
} from '../components/container/forms/types/api/graph.types';
import { Permission, PermissionEntry } from '../types';
import { WorkflowRegistry } from '../types/workflow.types';
import { FormDataType } from '@tw/tw-component-library';
import { createLogger } from './logger';

// Import schemas
// (None required here; individual components manage their own schemas)

// Create logger instance for workflow registry
const logger = createLogger('WorkflowRegistry');

// Simple submit handler that logs data - actual submission handled by useFormHandlers
export const createWorkflowSubmitHandler = (
  workflowId: string,
  sectionKey: string,
  workflowType: PaymentWorkflowType,
) => {
  return async (data: FormDataType): Promise<void> => {
    logger.debug('Workflow submit handler called', {
      workflowId,
      sectionKey,
      workflowType,
      data,
    });

    // The actual submission logic is handled by useFormHandlers
    logger.debug('Workflow submit handler completed');
  };
};

// Workflow configurations
const createDubaiPaymentApprovalConfig = () => ({
  id: 'dubai-payment-approval',
  name: 'Client Account Payment Request',
  icon: '📝',
  description: 'Dubai Office',
  workflowType: PaymentWorkflowType.DubaiPaymentApproval,
  office: 'M1',
  country: 'Dubai',
  apiEndpoint: '/api/dubai-payment',
  schema: undefined,
  isMultiSection: true,
  hasForm: true,
  permissions: false,
  handleSubmit: async (data: FormDataType) => {
    logger.debug('Client Account Payment Request submitted', { data });
    // The actual submission is handled by useFormHandlers
  },
  columns: [
    { key: 'id', header: 'ID', sortable: true, filterable: false },
    { key: 'name', header: 'Name', sortable: true, filterable: false },
    {
      key: 'currency',
      header: 'Currency',
      sortable: true,
      filterable: false,
    },
    {
      key: 'paymentTotal',
      header: 'Total Payment',
      sortable: true,
      filterable: false,
    },
    {
      key: 'status',
      header: 'Current State',
      sortable: true,
      filterable: false,
    },
    {
      key: 'createdByName',
      header: 'Requested By',
      sortable: true,
      filterable: true,
    },
    {
      key: 'currentSectionAssignments',
      header: 'Assigned To',
      sortable: true,
      filterable: true,
    },
    {
      key: 'createdAt',
      header: 'Created',
      type: 'date' as const,
      sortable: true,
      filterable: false,
    },
  ],
});

const createDubaiPaymentFromOfficeAccConfig = () => ({
  id: 'dubai-payment-from-office-acc',
  name: 'Payment From Office Account',
  icon: '💰',
  description: 'Dubai Office',
  workflowType: PaymentWorkflowType.DubaiPaymentFromOfficeAcc,
  office: 'M1',
  country: 'Dubai',
  apiEndpoint: '/api/office-account',
  schema: undefined, // Schema will be handled by the component using useDubaiPaymentFromOfficeAccountSchema
  isMultiSection: true,
  hasForm: true,
  permissions: false,
  handleSubmit: undefined, // Will be handled by useFormHandlers
  columns: [
    { key: 'id', header: 'ID', sortable: true, filterable: false },
    { key: 'name', header: 'Name', sortable: true, filterable: false },
    {
      key: 'paymentTotal',
      header: 'Total Payment',
      sortable: true,
      filterable: false,
    },
    {
      key: 'status',
      header: 'Current State',
      sortable: true,
      filterable: false,
    },
    {
      key: 'createdByName',
      header: 'Requested By',
      sortable: true,
      filterable: true,
    },
    {
      key: 'currentSectionAssignments',
      header: 'Assigned To',
      sortable: true,
      filterable: true,
    },
    {
      key: 'createdAt',
      header: 'Created',
      type: 'date' as const,
      sortable: true,
      filterable: false,
    },
  ],
});

const createOvertimeApprovalConfig = () => ({
  id: 'overtime-approval',
  name: 'Overtime Approval',
  icon: '⏰',
  description: 'UK Office',
  workflowType: PaymentWorkflowType.OvertimeApproval,
  office: 'UX',
  country: 'UK Office',
  apiEndpoint: '/api/overtime-approval',
  schema: undefined,
  isMultiSection: true,
  hasForm: true,
  permissions: false,
  handleSubmit: undefined,
  columns: [
    { key: 'id', header: 'ID', sortable: true, filterable: false },
    { key: 'name', header: 'Name', sortable: true, filterable: false },
    {
      key: 'status',
      header: 'Current State',
      sortable: true,
      filterable: false,
    },
    {
      key: 'createdByName',
      header: 'Requested By',
      sortable: true,
      filterable: true,
    },
    {
      key: 'currentSectionAssignments',
      header: 'Assigned To',
      sortable: true,
      filterable: true,
    },
    {
      key: 'createdAt',
      header: 'Created',
      type: 'date' as const,
      sortable: true,
      filterable: false,
    },
  ],
});

const createDubaiTransferRequisitionConfig = () => ({
  id: 'dubai-transfer-requisition',
  name: 'Transfer Requisition',
  icon: '💸',
  description: 'Dubai Office',
  workflowType: PaymentWorkflowType.DubaiTransferRequisition,
  office: 'M1',
  country: 'Dubai',
  apiEndpoint: '/api/transfer-requisition',
  schema: undefined,
  isMultiSection: true,
  hasForm: true,
  permissions: false,
  handleSubmit: undefined, // Will be handled by useFormHandlers
  columns: [
    { key: 'id', header: 'ID', sortable: true, filterable: false },
    { key: 'name', header: 'Name', sortable: true, filterable: false },
    {
      key: 'paymentTotal',
      header: 'Total Payment',
      sortable: true,
      filterable: false,
    },
    {
      key: 'status',
      header: 'Current State',
      sortable: true,
      filterable: false,
    },
    {
      key: 'createdByName',
      header: 'Requested By',
      sortable: true,
      filterable: true,
    },
    {
      key: 'currentSectionAssignments',
      header: 'Assigned To',
      sortable: true,
      filterable: true,
    },
    {
      key: 'createdAt',
      header: 'Created',
      type: 'date' as const,
      sortable: true,
      filterable: false,
    },
  ],
});

const createGiftHospitalityDonationConfig = () => ({
  id: 'gift-hospitality-donation-uk',
  name: 'Gift/Hospitality/Donation (UK)',
  icon: '🎁',
  description: 'UK Office',
  workflowType: PaymentWorkflowType.GiftHospitalityDonationUk,
  office: 'UX',
  country: 'UK Office',
  // TODO(workflow-generator): Set this when the backend endpoint is confirmed.
  apiEndpoint: '',
  schema: undefined,
  isMultiSection: true,
  hasForm: true,
  permissions: false,
  handleSubmit: undefined,
  columns: [
    {
      key: 'id',
      header: 'ID',
      type: 'number' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'name',
      header: 'Name',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'status',
      header: 'Current State',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'createdByName',
      header: 'Requested By',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'currentSectionAssignments',
      header: 'Authoriser',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'createdAt',
      header: 'Created',
      type: 'date' as const,
      sortable: true,
      filterable: true,
    },
  ],
});

const createGiftHospitalityDonationDubaiConfig = () => ({
  id: 'gift-hospitality-donation-dubai',
  name: 'Gift/Hospitality/Donation (Dubai)',
  icon: '🎁',
  description: 'Dubai',
  workflowType: PaymentWorkflowType.GiftHospitalityDonationDubai,
  office: 'M1',
  country: 'Dubai',
  // TODO(workflow-generator): Set this when the backend endpoint is confirmed.
  apiEndpoint: '',
  schema: undefined,
  isMultiSection: true,
  hasForm: true,
  permissions: false,
  handleSubmit: undefined,
  columns: [
    {
      key: 'id',
      header: 'ID',
      type: 'number' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'name',
      header: 'Name',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'status',
      header: 'Current State',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'createdByName',
      header: 'Requested By',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'authoriser',
      header: 'Authoriser',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'createdAt',
      header: 'Created',
      type: 'date' as const,
      sortable: true,
      filterable: true,
    },
  ],
});
const createGiftHospitalityDonationDublinConfig = () => ({
  id: 'gift-hospitality-donation-dublin',
  name: 'Gift/Hospitality/Donation (Dublin)',
  icon: '🎁',
  description: 'Dublin',
  workflowType: PaymentWorkflowType.GiftHospitalityDonationDublin,
  office: 'IE01',
  country: 'Dublin',
  // TODO(workflow-generator): Set this when the backend endpoint is confirmed.
  apiEndpoint: '',
  schema: undefined,
  isMultiSection: true,
  hasForm: true,
  permissions: false,
  handleSubmit: undefined,
  columns: [
    {
      key: 'id',
      header: 'ID',
      type: 'number' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'name',
      header: 'Name',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'status',
      header: 'Current State',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'createdByName',
      header: 'Requested By',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'authoriser',
      header: 'Authoriser',
      type: 'text' as const,
      sortable: true,
      filterable: true,
    },
    {
      key: 'createdAt',
      header: 'Created',
      type: 'date' as const,
      sortable: true,
      filterable: true,
    },
  ],
});
// PLOP_INJECT_WORKFLOW_CONFIG

export const workflowsRegistry: WorkflowRegistry = {
  'dubai-payment-approval': createDubaiPaymentApprovalConfig(),
  'dubai-payment-from-office-acc': createDubaiPaymentFromOfficeAccConfig(),
  'dubai-transfer-requisition': createDubaiTransferRequisitionConfig(),
  'overtime-approval': createOvertimeApprovalConfig(),
  'gift-hospitality-donation-uk': createGiftHospitalityDonationConfig(),
  'gift-hospitality-donation-dubai': createGiftHospitalityDonationDubaiConfig(),
  'gift-hospitality-donation-dublin':
    createGiftHospitalityDonationDublinConfig(),
  // PLOP_INJECT_WORKFLOW_REGISTRY
};

// Helper functions
export const getWorkflowByKey = (key: string) => {
  // Direct registry lookup first
  if (workflowsRegistry[key]) {
    return workflowsRegistry[key];
  }

  // Check if it matches a section key format
  const sectionKeyMatch = Object.keys(SECTION_KEY_MAP).find(
    (sectionKey) => sectionKey === key || SECTION_KEY_MAP[sectionKey] === key,
  );

  if (sectionKeyMatch) {
    return workflowsRegistry[sectionKeyMatch];
  }

  // Check if it matches a form name format (camelCase/PascalCase)
  const formNameMatch = Object.keys(FORM_TO_SECTION_KEY_MAP).find(
    (formName) => formName === key || FORM_TO_SECTION_KEY_MAP[formName] === key,
  );

  if (formNameMatch) {
    const sectionKey = FORM_TO_SECTION_KEY_MAP[formNameMatch];
    return workflowsRegistry[sectionKey];
  }

  // Fallback: try to find by workflow type name
  const workflowByType = Object.values(workflowsRegistry).find(
    (workflow) =>
      workflow.name.toLowerCase().replace(/\s+/g, '') ===
        key.toLowerCase().replace(/\s+/g, '') ||
      workflow.id === key ||
      workflow.workflowType.toString() === key,
  );

  return workflowByType || null;
};

export const getAllWorkflows = () => Object.values(workflowsRegistry);
export const getWorkflowKeys = () => Object.keys(workflowsRegistry);

/**
 * Testing: when a registry entry sets `permissions: true`, treat the user as
 * having full create/view access for that workflow type.
 */
export const isWorkflowPermissionsBypassed = (
  workflowType: PaymentWorkflowType,
): boolean =>
  Object.values(workflowsRegistry).some(
    (workflow) =>
      workflow.workflowType === workflowType && workflow.permissions === true,
  );

/**
 * Maps workflow types to their corresponding CREATE permission strings
 * Aligned with the Permission enum in src/types/index.ts
 */
const WORKFLOW_TYPE_TO_PERMISSION_MAP: Record<PaymentWorkflowType, string> = {
  [PaymentWorkflowType.Undefined]: '',
  [PaymentWorkflowType.DubaiPaymentApproval]:
    Permission.CreateDubaiPaymentApproval,
  [PaymentWorkflowType.DubaiTransferRequisition]:
    Permission.CreateDubaiTransferRequisition,
  [PaymentWorkflowType.DubaiPaymentFromOfficeAcc]:
    Permission.CreateDubaiPaymentFromOfficeAccount,
  [PaymentWorkflowType.OvertimeApproval]: Permission.CreateOvertimeApproval,
  [PaymentWorkflowType.GiftHospitalityDonationUk]:
    Permission.CreateGiftHospitalityDonationUk,
  [PaymentWorkflowType.GiftHospitalityDonationDubai]:
    Permission.CreateGiftHospitalityDonationDubai,
  [PaymentWorkflowType.GiftHospitalityDonationDublin]:
    Permission.CreateGiftHospitalityDonationDublin,
  // PLOP_INJECT_WORKFLOW_PERMISSION_MAP
};

/**
 * Checks if a user can create a specific workflow type
 * @param workflowType The workflow type to check
 * @param canCreateWorkflowTypes Array of permission strings the user has
 * @returns boolean indicating if the user can create this workflow type
 */
export const canUserCreateWorkflow = (
  workflowType: PaymentWorkflowType,
  canCreateWorkflowTypes: (string | number)[],
  permissionEntries?: PermissionEntry[],
): boolean => {
  if (isWorkflowPermissionsBypassed(workflowType)) {
    return true;
  }

  const requiredPermission = WORKFLOW_TYPE_TO_PERMISSION_MAP[workflowType];

  // Check explicit create-permission strings
  const hasCreatePermissionString =
    canCreateWorkflowTypes.includes(requiredPermission);

  // Check detailed permission entries by string value
  const hasCreatePermissionEntry = Boolean(
    permissionEntries?.some((p) => p.permission === requiredPermission),
  );

  return hasCreatePermissionString || hasCreatePermissionEntry;
};

/**
 * Filters workflows based on user permissions
 * @param workflows Array of workflows to filter
 * @param canCreateWorkflowTypes Array of permission strings the user has
 * @returns Filtered array of workflows the user can create
 */
export const filterWorkflowsByUserPermissions = (
  workflows: ReturnType<typeof getAllWorkflows>,
  canCreateWorkflowTypes: (string | number)[],
  permissionEntries?: PermissionEntry[],
) => {
  return workflows.filter((workflow) =>
    canUserCreateWorkflow(
      workflow.workflowType,
      canCreateWorkflowTypes,
      permissionEntries,
    ),
  );
};

/**
 * Gets the office for a specific workflow type
 * @param workflowType The workflow type to get office for
 * @returns The office string for the workflow type
 */
export const getOfficeForWorkflowType = (
  workflowType: PaymentWorkflowType,
): string => {
  const workflow = Object.values(workflowsRegistry).find(
    (w) => w.workflowType === workflowType,
  );
  return workflow?.office || 'UX'; // Default fallback
};

/**
 * Gets the office for a workflow registry key
 * @param key Workflow id, section key, or form name
 * @returns The office string for the workflow, if configured
 */
export const getOfficeForWorkflowKey = (key: string): string | undefined => {
  return getWorkflowByKey(key)?.office;
};

// Factory function to create workflow configurations with auth access
export const createWorkflowWithAuth = (id: string, workflowKey: string) => {
  logger.debug('createWorkflowWithAuth called', { id, workflowKey });

  const baseWorkflow = workflowsRegistry[workflowKey];

  if (!baseWorkflow) {
    logger.error(`Workflow ${workflowKey} not found`);
    throw new Error(`Workflow ${workflowKey} not found`);
  }

  const sectionKey = SECTION_KEY_MAP[workflowKey] || workflowKey;
  const workflowType = baseWorkflow.workflowType;

  logger.debug('Using section key', { sectionKey });
  logger.debug('Original handleSubmit exists', {
    exists: !!baseWorkflow.handleSubmit,
  });

  const workflowWithAuth = {
    ...baseWorkflow,
    handleSubmit: createWorkflowSubmitHandler(id, sectionKey, workflowType),
  };

  logger.debug('New handleSubmit created', {
    exists: !!workflowWithAuth.handleSubmit,
  });

  return workflowWithAuth;
};
